import React, { useCallback, useState } from 'react';
import { Button, Col, Row } from 'reactstrap';
import { connect, useSelector } from 'react-redux';
import _ from 'lodash';
import Dropzone from 'react-dropzone';
import { Field } from 'react-final-form';

import { Intl, ModalForm } from 'components';
import MediaAudioService from 'api/MediaAudio';
import SettingsActions from 'store/reducers/Settings';
import { handlePromise } from 'utils';
import { required } from 'config/InputErrors';

const UploadAudioModal = ( {
  open, onClose, onReload, toggleLoading,
} ) => {
  const userData = useSelector( ( state ) => state.user );
  const { locale } = useSelector( ( state ) => state.settings.locale );
  const [localError, setLocalErrors] = useState( null );

  const errMsg = useCallback( ( seconds ) => {
    if ( locale === 'en' ) {
      return `This audio should take less than ${seconds} seconds`;
    }
    return `Este audio no debe superar los ${seconds} segundos`;
  }, [locale] );

  const uploadFile = useCallback( async ( dataToSend ) => {
    toggleLoading( true );
    const [errors, response] = await handlePromise(
      MediaAudioService.saveMediaAudio( dataToSend.file ),
    );
    toggleLoading( false );

    if ( !response.ok ) {
      return _.isObject( errors )
        ? _.join( _.map( errors, ( error, key ) => `${key}: ${error}` ), '<br/>' ) : errors;
    }
    onReload();
  }, [onReload, toggleLoading] );

  return (
    <ModalForm
      showErrorsInHeader
      size="md"
      open={open}
      onClose={onClose}
      onSubmit={uploadFile}
      body={( modalForm ) => {
        const modalState = modalForm.getState();
        return (
          <>
            <Field name="file" validate={required}>
              {( { input } ) => (
                <>
                  <Dropzone
                    maxSize={1048576}
                    accept=".mp3"
                    onDrop={( files ) => {
                      if ( files && files[0] ) {
                        const reader = new FileReader();
                        reader.readAsDataURL( files[0] );
                        reader.onload = ( event ) => {
                          const audio = document.createElement( 'audio' );
                          audio.src = event.target.result;
                          audio.addEventListener( 'loadedmetadata', () => {
                            if ( userData && userData.simpleMessageLimit
                              ? audio.duration > userData.simpleMessageLimit
                              : audio.duration > 60 ) {
                              input.onChange( null );
                              // setLocalErrors( 'invalidAudioDuration' );
                              setLocalErrors( errMsg( userData && userData.simpleMessageLimit
                                ? userData.simpleMessageLimit
                                : 60 ) );
                            } else {
                              setLocalErrors( null );
                              input.onChange( {
                                mediaFile: files[0],
                                originalName: files[0].name,
                                time: parseInt( audio.duration, 10 ) === 0
                                  ? 1 : parseInt( audio.duration, 10 ),
                              } );
                            }
                          }, false );
                        };
                      } else {
                        input.onChange( null );
                        setLocalErrors( 'invalidFormat' );
                      }
                    }}
                  >
                    {( { getRootProps, getInputProps } ) => (
                      <Row className="mt-4">
                        <Col md={10} className="m-auto" onClick={getRootProps().onClick}>
                          <input {...getInputProps()} />
                          <Button
                            color="app-default"
                            size="app-sm"
                            type="button"
                            block
                          >
                            <Intl id="selectFile" />
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </Dropzone>

                  <Row className="mt-2">
                    <Col md={10} className="m-auto">
                      <p className="text-center text-sm text-mp-orange-1 mb-0">
                        {input.value ? input.value.originalName || input.value.name : <br />}
                      </p>
                    </Col>
                  </Row>

                  {!!localError
                  && <div className="error mt-1 text-md text-center">{localError.indexOf( ' ' ) >= 0 ? localError : <Intl id={localError} />}</div>}
                </>
              )}
            </Field>
            {modalState.submitFailed && modalState.errors.file
            && <div className="error mt-1 text-md text-center"><Intl id="campaignOneAudioError" /></div>}
          </>
        );
      }}
      footer={(
        <Button
          color="app-default"
          size="app-sm"
          className="font-weight-700"
        >
          <Intl id="okBtn" />
        </Button>
      )}
    />
  );
};

const mapDispatchToProps = ( {
  toggleLoading: SettingsActions.toggleLoading,
} );

export default connect( null, mapDispatchToProps )( UploadAudioModal );
